import { getAppUserMenus, getAppUserMenuPageAttributes } from '@/api/endpoints';
import { AppMenuService } from '@/service';
import { AppMenu, AppUser, AppUserMenu, AppUserMenuPageAttribute } from '@/api/models';
import { getUserMenuSettings } from './MenuSettingsHelper';
import { getTourPageSettings } from './TourPageSettingsHelper';
import { getModuleSettings } from './ModuleSettingsHelper';
import { getTourContractSettings } from './ContractSettingsHelper';
import { getTourPlayersSettings } from './TourPlayerSettingsHelper';
import { onlineStatus } from '@/utils';
import { getTourBagSettings } from './TourBagSettingsHelper';
import { getUser, setUser } from './UserSessionHelper';
import { ordersReportName, tourReportName } from '@/constants';
import { getReportSettings } from './ReportSettingsHelper';

type UserMenuSchemaKey = 'Tour' | 'MasterList' | 'Shaft' | 'Admin' | 'Order' | 'Inventory' | 'WGR' | 'Contract' | 'Players' | 'PlayerAdd' | 'PlayerEdit' | 'TourBag';

interface UserMenuItem {
  name: string;
  isParentMenu: boolean;
  appMenuId?: number;
  parentAppMenuId?: number | null;
}

export type UserMenuSchema = Record<UserMenuSchemaKey, UserMenuItem>;

const userMenuSchema: UserMenuSchema = {
  Tour: {
    name: 'Tour',
    isParentMenu: true,
  },
  MasterList: {
    name: 'MasterList',
    isParentMenu: true,
  },
  Shaft: {
    name: 'Shaft',
    isParentMenu: true,
  },
  Admin: {
    name: 'Admin',
    isParentMenu: true,
  },
  Order: {
    name: 'Order',
    isParentMenu: true,
  },
  Inventory: {
    name: 'Inventory',
    isParentMenu: true,
  },
  WGR: {
    name: 'WGR',
    isParentMenu: false,
  },
  Contract: {
    name: 'Contract',
    isParentMenu: true,
  },
  Players: {
    name: 'Players',
    isParentMenu: true,
  },
  PlayerAdd: {
    name: 'PlayerAdd',
    isParentMenu: false,
  },
  PlayerEdit: {
    name: 'PlayerEdit',
    isParentMenu: false,
  },
  TourBag: {
    name: 'Tour Bag',
    isParentMenu: true,
  },
};

const addIdToAppMenu = (appMenus: AppMenu[]) => {
  Object.entries(userMenuSchema).forEach(([key, menu]) => {
    const matchedMenu = appMenus.find((appMenu) => appMenu.name === menu.name);
    if (matchedMenu) {
      userMenuSchema[key as UserMenuSchemaKey].appMenuId = matchedMenu.appMenuId;
      userMenuSchema[key as UserMenuSchemaKey].parentAppMenuId = matchedMenu.parentAppMenuId;
    } else {
      throw new Error(`No matching appMenu found for ${menu.name}`);
    }
  });
};

// We don't use a service since we don't have a TTL for permissions, we fetch every time its online or fallback to the IndexedDB data
const fetchUserPermissions = async (appUser: AppUser) => {
  const userId = appUser.appUserId.toString();
  let userMenus: AppUserMenu[] = [];
  let userPageAttributes: AppUserMenuPageAttribute[] = [];
  let appMenus: AppMenu[] = [];

  if (!appUser.appUserId) throw new Error('Failed to fetch the user id');

  try {
    const [menus, attributes, menusData] = await Promise.all([
      // TODO: Fetch user permissions with token instead of user id
      getAppUserMenus(userId), // Which menus to show
      getAppUserMenuPageAttributes(userId), // Readonly / LimitedView
      AppMenuService.getAppMenuData(), // All app menus metadata with service logic
    ]);

    userMenus = menus;
    userPageAttributes = attributes;
    appMenus = menusData;
  } catch (error) {
    console.error('Failed to fetch user permissions from API:', error);
    // TODO: Save error to ErrorLogRepo
  }

  return {
    appUser,
    userMenus,
    userPageAttributes,
    appMenus,
  };
};

export const setUserPermissions = async (appUser: AppUser) => {
  if (!onlineStatus.get()) return;

  const { userMenus, userPageAttributes, appMenus } = await fetchUserPermissions(appUser);

  addIdToAppMenu(appMenus); // inject appMenuId into sidebarMenu

  const menuSettings = getUserMenuSettings({ userMenus, appMenus, userMenuSchema });
  let appMenuId = userMenuSchema.Tour.appMenuId || 0;
  const tourReportSettings = getReportSettings({ reportNames: tourReportName, appMenuId, appMenus, userMenus });
  const tourPageSettings = getTourPageSettings(userPageAttributes);
  const moduleSettings = getModuleSettings({ appMenus, userMenus });
  const tourContractSettings = getTourContractSettings({ appMenus, userMenus, userMenuSchema });
  const tourPlayerSettings = getTourPlayersSettings({ userMenus, userMenuSchema });
  const tourBagSettings = getTourBagSettings({ appMenus, userMenus, userMenuSchema });
  appMenuId = userMenuSchema.Order.appMenuId || 0;
  const ordersReportSettings = getReportSettings({ reportNames: ordersReportName, appMenuId, appMenus, userMenus });
  const user = await getUser();
  if (user) {
    user.appUserId = appUser?.appUserId;
    // user.subnavOrder = appUser?.subnavOrder;
    user.menuSettings = menuSettings;
    user.tourReportSettings = tourReportSettings;
    user.tourPageSettings = tourPageSettings;
    user.moduleSettings = moduleSettings;
    user.contractSettings = tourContractSettings;
    user.tourPlayerSettings = tourPlayerSettings;
    user.tourBagSettings = tourBagSettings;
    user.ordersReportSettings = ordersReportSettings;
    await setUser(user);
  }
};
